import { useId, useState } from 'react';
import type { Problem, StageCondition } from '../api/types';
import { Button } from '../components/Button';
import { ProblemNotice } from '../components/ProblemNotice';
import { stageMoveProblemText } from '../app/status';
import { toProblem } from '../app/useAsync';
import { STAGE_LABELS } from './StageBar';

/**
 * 段階を進めたときに送る固定文（U7）。
 * AI に「段階 4 へ」と打たせる代わりに、画面が同じ意味の 1 文を送ってそのターンを始める。
 */
export function stageStartText(stage: number): string {
  return `段階 ${stage}（${STAGE_LABELS[stage - 1] ?? ''}）に進みました。この段階の作業を始めてください。`;
}

/**
 * 段階は進んだのに、続けて送るターンだけが失敗したときの知らせ（U7）。
 * 段階は戻さないので、何が起きて次に何をすればよいかを 1 文で伝える。
 */
export function turnNotStartedText(stage: number, detail: string): string {
  return `段階 ${stage}（${STAGE_LABELS[stage - 1] ?? ''}）に進みました。AI への指示は送れませんでした。入力欄から指示を送ってください（${detail}）`;
}

/**
 * 足りない条件を 1 行に畳む（段階バーのポップオーバーと同じ ○ ＋ label ＋ detail）。
 * すべて満たしていれば null（＝押せる）。条件をまだ読んでいない（0 件）ときも進めない（StageBar と同じ）。
 */
export function unmetText(conditions: StageCondition[]): string | null {
  if (conditions.length === 0) return '次へ進める条件をまだ読み込んでいません';
  const unmet = conditions.filter((condition) => !condition.met);
  if (unmet.length === 0) return null;
  return unmet
    .map((condition) => `○ ${condition.label}${condition.detail ? `（${condition.detail}）` : ''}`)
    .join('・');
}

export interface StageAdvanceProps {
  /** Draft の段階（1〜6）。null なら Draft が無い。 */
  stage: number | null;
  /** 現在の段階から次へ進む条件（GET A/draft/review の conditions。段階バーに渡すものと同じ）。 */
  conditions: StageCondition[];
  /** 段階の移動か固定文のターンの送信を要求中（段階バーと共有する StudioPage の stageBusy || advancing）。 */
  busy: boolean;
  /** 未反映の変更案がある。進めると head が動き、その変更案は 409 revision_conflict で反映できなくなる。 */
  proposalPending?: boolean;
  /**
   * 段階を 1 つ進めてから、固定文のターンを送る。
   * 段階の移動かターンの送信が失敗したら投げる（送信だけが失敗したときは段階は進んだまま）。
   */
  onAdvance(): Promise<void>;
}

/**
 * チャットの末尾（会話の下・入力欄の上）の「次の段階へ」のアクション行（U7）。
 * AI に「「段階 4 へ」とお伝えください」と打たせる代わりに、利用者がここを押して段階を進める。
 * 段階 6（最後）と Draft が無いときは出さない。作業中・回答待ち・fixture モードで出さないのは ChatPane が決める。
 * 段階バーの「段階の移動」ダイアログ（戻る操作を含む）はそのまま残る。
 */
export function StageAdvance({ stage, conditions, busy, proposalPending = false, onAdvance }: StageAdvanceProps) {
  const unmetId = useId();
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [shownStage, setShownStage] = useState(stage);
  if (shownStage !== stage) {
    // 段階が動いたら（段階バーで進めた・変更案を反映した）古い失敗は捨てる（StageBar と同じ扱い）。
    setShownStage(stage);
    setProblem(null);
  }
  // 段階 6 には次が無い。前に戻るのは段階バーのポップオーバーで行う。
  if (stage === null || stage < 1 || stage >= STAGE_LABELS.length) return null;

  const next = stage + 1;
  // 未反映の変更案は条件の不足より先に伝える（進めた後では、その変更案はもう反映できない）。
  const unmet = proposalPending ? '未反映の変更案があります。先に反映するか見送ってください' : unmetText(conditions);
  const press = async () => {
    // 二重押しを防ぐ（押した瞬間に無効にし、終わるまで戻さない）。
    if (sending || busy) return;
    setSending(true);
    setProblem(null);
    try {
      await onAdvance();
    } catch (error) {
      setProblem(toProblem(error));
    } finally {
      setSending(false);
    }
  };
  // ProblemNotice は文面を書き換えないので、段階の移動の失敗だけ detail を言い換える（段階バーと同じ文）。
  const notice = problem === null ? null : { ...problem, detail: stageMoveProblemText(problem) ?? problem.detail };

  return (
    <div className="studio-stage-advance" role="group" aria-label="次の段階へ">
      <Button
        variant="primary"
        disabled={busy || sending || unmet !== null}
        aria-describedby={unmet === null ? undefined : unmetId}
        onClick={() => void press()}
      >
        {`段階 ${next}（${STAGE_LABELS[next - 1] ?? ''}）へ進む`}
      </Button>
      {unmet !== null && (
        <p className="hint studio-stage-advance-unmet" id={unmetId}>
          {unmet}
        </p>
      )}
      <ProblemNotice
        problem={notice}
        tone={notice !== null && (notice.status === 409 || notice.status === 429) ? 'notice' : 'error'}
      />
    </div>
  );
}
